import { SITE } from "./site.js";

/** Canonical absolute URL. The home page is the bare origin, no trailing slash. */
export function absoluteUrl(path = "/") {
  if (/^https?:\/\//.test(path)) return path;
  if (path === "/" || path === "") return SITE.url;
  return `${SITE.url}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Image bases from content (`/images/x/hero`) resolve to the 1600px JPEG for Open Graph. */
function ogImageUrl(image) {
  if (!image) return absoluteUrl(SITE.defaultOgImage);
  if (/\.(jpg|jpeg|png|webp)$/i.test(image)) return absoluteUrl(image);
  return absoluteUrl(`${image}-1600.jpg`);
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.url,
    logo: absoluteUrl("/images/site/logo-square.png"),
    foundingDate: String(SITE.foundedYear),
    email: SITE.email,
    telephone: SITE.phone,
    sameAs: SITE.social.map((s) => s.href),
    address: SITE.offices.map((o) => ({
      "@type": "PostalAddress",
      addressLocality: o.city,
      addressRegion: o.region || undefined,
      addressCountry: o.country,
    })),
  };
}

export function articleJsonLd(entry, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: entry.title,
    description: entry.description,
    datePublished: entry.date,
    dateModified: entry.updated || entry.date,
    image: ogImageUrl(entry.image),
    mainEntityOfPage: absoluteUrl(path),
    author: { "@type": "Organization", name: entry.author || SITE.name },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: absoluteUrl("/images/site/logo-square.png") },
    },
  };
}

export function personJsonLd(entry, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: entry.name,
    jobTitle: entry.role,
    url: absoluteUrl(path),
    image: entry.photo ? absoluteUrl(`${entry.photo}-800.jpg`) : undefined, // portrait kind's largest JPEG
    worksFor: { "@type": "Organization", name: SITE.name },
    sameAs: entry.linkedin ? [entry.linkedin] : undefined,
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

/**
 * The one place page metadata is assembled. Returns the array React Router's
 * `meta()` export expects. The last matching route replaces the whole array,
 * so every route calls this with everything it needs.
 */
export function buildMeta({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  jsonLd = [],
}) {
  const fullTitle = path === "/" ? title : `${title} | ${SITE.name}`;
  const url = absoluteUrl(path);
  const imageUrl = ogImageUrl(image);
  const tags = [
    { title: fullTitle },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: url },
    { property: "og:type", content: type },
    { property: "og:site_name", content: SITE.name },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: imageUrl },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: imageUrl },
  ];
  if (noindex) tags.push({ name: "robots", content: "noindex, nofollow" });
  for (const ld of [organizationJsonLd(), ...jsonLd]) tags.push({ "script:ld+json": ld });
  return tags;
}
